import { ChangeDetectorRef, Component, EventEmitter, Input, Output, ViewChildren, QueryList } from '@angular/core';
import { IDocument, ViewMode } from 'src/app/models/document.models';
import { EventColors } from 'src/app/models/event-colors';
import { EmptyStateType } from '../document-empty-state/document-empty-state.component';
import { DocumentCardComponent } from '../document-card/document-card.component';

@Component({
  selector: 'app-document-list',
  templateUrl: './document-list.component.html',
  styleUrls: ['./document-list.component.scss'],
})
export class DocumentListComponent {
  @Input() documents: IDocument[] = [];
  @Input() language: string;
  @Input() colors: EventColors;
  @Input() loading: boolean = false;
  @Input() viewMode: ViewMode = 'list';
  @Input() showViewToggle: boolean = true;
  @Input() emptyStateType: EmptyStateType = 'no_documents';
  @Input() widthImg: number = 48;
  @Input() showNoResultImage: boolean = false;
  @Input() useVirtualScroll: boolean = true;
  @Input() selectionEnabled: boolean = true;
  @Output() viewModeChange = new EventEmitter<ViewMode>();
  @Output() documentPreview = new EventEmitter<IDocument>();
  @Output() downloadSelected = new EventEmitter<IDocument[]>();

  selectedIds: Set<string> = new Set();

  @ViewChildren(DocumentCardComponent) cards: QueryList<DocumentCardComponent>;

  constructor(private cdr: ChangeDetectorRef) {}

  readonly skeletonCount = 8;
  readonly skeletonArray = Array(this.skeletonCount);

  get isEmpty(): boolean {
    return !this.loading && this.documents.length === 0;
  }

  get effectiveEmptyType(): EmptyStateType {
    if (this.showNoResultImage) return 'no_results';
    return this.emptyStateType;
  }

  get selectedCount(): number {
    return this.selectedIds.size;
  }

  get allSelected(): boolean {
    return this.documents.length > 0 && this.selectedIds.size === this.documents.length;
  }

  get selectedDocs(): IDocument[] {
    return this.documents.filter(d => this.selectedIds.has(d.uid));
  }

  toggleView(): void {
    const newMode: ViewMode = this.viewMode === 'grid' ? 'list' : 'grid';
    this.viewModeChange.emit(newMode);
  }

  onDocumentPreview(doc: IDocument): void {
    this.documentPreview.emit(doc);
  }

  onSelectionChange(doc: IDocument, checked: boolean): void {
    if (!this.selectionEnabled) return;
    if (checked) {
      this.selectedIds.add(doc.uid);
    } else {
      this.selectedIds.delete(doc.uid);
    }
    this.selectedIds = new Set(this.selectedIds);
    this.cdr.detectChanges();
  }

  toggleSelectAll(): void {
    if (!this.selectionEnabled) return;
    if (this.allSelected) {
      this.selectedIds = new Set();
    } else {
      this.selectedIds = new Set(this.documents.map(d => d.uid));
    }
    this.cdr.detectChanges();
    this.cards?.forEach(card => card.syncSelected(this.selectedIds));
  }

  clearSelection(): void {
    if (!this.selectionEnabled) return;
    this.selectedIds = new Set();
    this.cdr.detectChanges();
    this.cards?.forEach(card => card.syncSelected(this.selectedIds));
  }

  onDownloadSelected(): void {
    if (!this.selectionEnabled || this.selectedDocs.length === 0) return;
    this.downloadSelected.emit(this.selectedDocs);
  }

  trackByUid(_index: number, doc: IDocument): string {
    return doc.uid;
  }
}
